// src/components/Minihome/MinihomeView.jsx

import { useEffect, useMemo, useState } from "react";
import { useParams } from "react-router-dom";
import { Box, CircularProgress, Alert } from "@mui/material";
import RGL, { WidthProvider } from "react-grid-layout";
import { useUser } from "../../contexts/UserContext";
import WidgetRegistry from "./WidgetRegistry";
import { getUserMinihome } from "../../api/minihome";

import "react-grid-layout/css/styles.css";
import "react-resizable/css/styles.css";

const GridLayout = WidthProvider(RGL);
const DEFAULT_GRID = { cols: 12, rowHeight: 20, margin: [8, 8] };

export default function MinihomeView() {
    const { username } = useParams();
    const { userInfo } = useUser(); // 현재 로그인한 "나"
    const [home, setHome] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState("");

    useEffect(() => {
        (async () => {
            setLoading(true); setError("");
            try {
                const { data } = await getUserMinihome(username);
                setHome(data);
            } catch (err) {
                console.error("미니홈 로드 실패:", err);
                setError("미니홈 정보를 불러오는 데 실패했습니다.");
            } finally { setLoading(false); }
        })();
    }, [username]);

    const layout = useMemo(() => {
        const items = home?.layout?.items;
        if (!Array.isArray(items) || items.length === 0) {
            // ... (기본 레이아웃 부분은 그대로)
            return [
                { i: "about-default", type: "about", x: 0, y: 0, w: 4, h: 8, backgroundColor: 'rgba(255,255,255,0.8)', introText: '안녕하세요!' },
                { i: "guestbook-default", type: "guestbook", x: 4, y: 0, w: 8, h: 12, backgroundColor: 'rgba(255,255,255,0.8)' },
            ];
        }
        return items.map((item, index) => ({
            i: String(item.i || `w-${index}`),
            type: item.type || 'about',
            x: item.x || 0, y: item.y || 0, w: item.w || 4, h: item.h || 6,
            backgroundColor: item.backgroundColor || 'rgba(255,255,255,0.8)',
            introText: item.introText || '',
            isTransparent: item.isTransparent || false,
            showcaseConfig: item.showcaseConfig || null,
            emoteConfig: item.emoteConfig || null,
            // ✅ 수정 1: reactionConfig 속성을 추가하여 ReactionBar 위젯으로 전달되도록 합니다.
            reactionConfig: item.reactionConfig || null,
            textConfig: item.textConfig || null,
        }));
    }, [home]);

    const gridConfig = useMemo(() => home?.layout?.grid || DEFAULT_GRID, [home]);

    const backgroundStyle = useMemo(() => {
        const bg = home?.theme?.background || {};
        const baseStyle = {
            width: '100%',
            height: '100%',
            minHeight: '100vh',
            position: 'relative',
            backgroundColor: bg.type === 'color' ? bg.value : '#ffffff',
        };

        if (bg.type === 'emote' && bg.emote) {
            const isUrl = bg.emote.startsWith('/') || bg.emote.startsWith('http');
            const size = bg.size || 48;
            const opacity = bg.opacity || 0.1;
            const repeatMode = bg.repeatMode || 'pattern';

            let imageUrl = bg.emote;
            if (!isUrl) {
                const svg = `<svg xmlns="http://www.w3.org/2000/svg" width="${size * 1.5}" height="${size * 1.5}"><text x="50%" y="50%" dominant-baseline="middle" text-anchor="middle" font-size="${size}px">${bg.emote}</text></svg>`;
                imageUrl = `url("data:image/svg+xml,${encodeURIComponent(svg)}")`;
            } else {
                imageUrl = `url(${imageUrl})`;
            }

            // ✅ 'cover' 모드 추가
            if (repeatMode === 'cover') {
                return {
                    ...baseStyle,
                    '&::before': {
                        content: '""',
                        position: 'absolute',
                        top: 0, left: 0, right: 0, bottom: 0,
                        backgroundImage: imageUrl,
                        backgroundRepeat: 'no-repeat',
                        backgroundPosition: 'center center',
                        backgroundSize: 'cover', // ✅ 화면을 꽉 채우는 핵심 속성
                        opacity: opacity,
                        zIndex: 0,
                    }
                };
            }

            if (repeatMode === 'single') {
                return {
                    ...baseStyle,
                    '&::before': {
                        content: '""',
                        position: 'absolute',
                        top: 0, left: 0, right: 0, bottom: 0,
                        backgroundImage: imageUrl,
                        backgroundRepeat: 'no-repeat',
                        backgroundPosition: 'center center',
                        backgroundSize: 'contain',
                        opacity: opacity,
                        zIndex: 0,
                    }
                };
            }

            // 'pattern' 모드 (기존 로직)
            return {
                ...baseStyle,
                '&::before': {
                    content: '""',
                    position: 'absolute',
                    top: 0, left: 0, right: 0, bottom: 0,
                    backgroundImage: imageUrl,
                    backgroundRepeat: 'repeat',
                    backgroundSize: `${size * 1.5}px ${size * 1.5}px`,
                    opacity: opacity,
                    zIndex: 0,
                }
            };
        }
        return baseStyle;
    }, [home]);

    // ✅ 3. 현재 로그인한 유저가 이 미니홈의 주인인지 확인합니다.
    const isOwner = useMemo(() => {
        // home 데이터가 로드된 후 (home.username) 비교하는 것이 더 정확합니다.
        if (!home || !userInfo) return false;
        return home.username === userInfo.username;
    }, [home, userInfo]); // home과 userInfo가 바뀔 때마다 재계산


    if (loading) { return <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}><CircularProgress /></Box>; }
    if (error) { return <Box sx={{ p: 2 }}><Alert severity="error">{error}</Alert></Box>; }
    if (!home) { return null; }

    return (
        <Box sx={{ p: 2, ...backgroundStyle }}>
            <GridLayout className="layout" layout={layout} cols={gridConfig.cols} rowHeight={gridConfig.rowHeight} margin={gridConfig.margin} isDraggable={false} isResizable={false} compactType={null}>
                {layout.map(item => {
                    const Comp = WidgetRegistry[item.type];
                    return (
                        <div key={item.i} style={{
                            zIndex: 1,
                            background: item.isTransparent ? 'transparent' : item.backgroundColor
                        }}>
                            {Comp ? <Comp
                                home={home}
                                item={item}
                                // ✅ 4. canModerate={false} 대신 isOwner 값을 전달합니다.
                                canModerate={isOwner}
                                username={home.username}
                            /> : `알 수 없는 위젯: ${item.type}`}
                        </div>
                    );
                })}
            </GridLayout>
        </Box>
    );
}